import { useEffect, useMemo, useState } from "react";
import { Reservation, Table, CombinedTable } from "@/types/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useFloorPlanDrag } from "@/contexts/FloorPlanDragContext";
import { V2ReservationCard } from "./V2ReservationCard";
import {
  Users, UtensilsCrossed, MessageSquare, GripVertical, Baby,
  StickyNote, AlertCircle, Check, X, CalendarClock, ChevronDown, ChevronRight,
  Clock as ClockIcon, Lock, Unlock,
} from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";
import { parseTimeToMinutes } from "@/lib/timeBookingUtils";
import { getSlotColor } from "@/lib/slotColors";
import { useServiceTimeCompare } from "@/hooks/useServiceTimeCompare";
import { reservationOverlapsSlot } from "@/lib/serviceOrder";

interface ReservationSettings {
  bookingMode: 'slot' | 'time';
  reservationDurationMinutes: number;
}

interface Props {
  selectedDate: Date;
  selectedTime: string | null;
  slotEnd?: string;
  reservations: Reservation[];
  tables: Table[];
  combinedTables: CombinedTable[];
  reservationSettings?: ReservationSettings;
  highlightedReservationId: string | null;
  onHoverReservation: (resId: string | null) => void;
  onSelectReservation: (res: Reservation) => void;
  onEditReservation?: (res: Reservation) => void;
  onStatusChange?: (res: Reservation, status: "arrivé" | "annulé" | "planifié") => void;
  onTableChange?: (reservationId: string, tableId: string | null, combinedTableId: string | null) => void;
  onLockChange?: (reservationId: string, locked: boolean) => void;
}

const LATE_THRESHOLD_MIN = 15;

const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const overlaps = (
  resStart: string,
  resDur: number,
  slotStart: string,
  slotEnd?: string,
) => {
  if (!slotEnd) return resStart.substring(0, 5) === slotStart;
  return reservationOverlapsSlot(resStart.substring(0, 5), resDur, slotStart, slotEnd);
};

const isLate = (r: Reservation, today: Date, nowMin: number): boolean => {
  if (r.status === 'annulé' || r.status === 'arrivé') return false;
  if (!r.date) return false;
  const d = r.date instanceof Date ? r.date : new Date(r.date);
  if (dayKey(d) !== dayKey(today)) return false;
  const [h, m] = r.startTime.substring(0, 5).split(':').map(Number);
  return nowMin - (h * 60 + m) > LATE_THRESHOLD_MIN;
};


export const V2ReservationPanel = ({
  selectedDate, selectedTime, slotEnd,
  reservations, tables, combinedTables, reservationSettings,
  highlightedReservationId, onHoverReservation, onSelectReservation,
  onEditReservation, onStatusChange, onTableChange, onLockChange,
}: Props) => {
  const compareServiceTime = useServiceTimeCompare();
  const { dragState, startDrag, endDrag } = useFloorPlanDrag();
  const [onlySlot, setOnlySlot] = useState(true);
  const [hideCancelled, setHideCancelled] = useState(true);
  const [collapsedSlots, setCollapsedSlots] = useState<Set<string>>(new Set());

  // Refresh "late" detection every 30s
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const today = useMemo(() => new Date(nowTick), [nowTick]);
  const nowMin = today.getHours() * 60 + today.getMinutes();
  const isToday = dayKey(today) === dayKey(selectedDate);

  const dayReservations = useMemo(() => {
    const k = dayKey(selectedDate);
    return reservations.filter(r => r.date && dayKey(r.date instanceof Date ? r.date : new Date(r.date)) === k);
  }, [reservations, selectedDate]);

  const defaultDur = reservationSettings?.reservationDurationMinutes ?? 120;
  const isTime = reservationSettings?.bookingMode === 'time';

  const filtered = useMemo(() => {
    let list = dayReservations;
    if (hideCancelled) list = list.filter(r => r.status !== 'annulé');
    if (onlySlot && selectedTime) {
      list = list.filter(r => {
        if (isTime) {
          const dur = r.durationMinutes ?? defaultDur;
          return overlaps(r.startTime, dur, selectedTime, slotEnd);
        }
        return r.startTime.substring(0, 5) === selectedTime;
      });
    }
    return [...list].sort((a, b) => {
      const aUn = !a.tableId && !a.combinedTableId ? 0 : 1;
      const bUn = !b.tableId && !b.combinedTableId ? 0 : 1;
      if (aUn !== bUn) return aUn - bUn;
      const at = compareServiceTime(a.startTime, b.startTime);
      if (at !== 0) return at;
      return (a.customerLastName || '').localeCompare(b.customerLastName || '');
    });
  }, [dayReservations, hideCancelled, onlySlot, selectedTime, slotEnd, isTime, defaultDur, compareServiceTime]);

  // A3 — Header counters (over the *visible* list)
  const counters = useMemo(() => {
    let arrived = 0, upcoming = 0, late = 0, covers = 0;
    for (const r of filtered) {
      if (r.status === 'annulé') continue;
      covers += r.numberOfPeople;
      if (r.status === 'arrivé') { arrived++; continue; }
      if (isToday && isLate(r, today, nowMin)) { late++; continue; }
      upcoming++;
    }
    return { arrived, upcoming, late, covers };
  }, [filtered, isToday, today, nowMin]);

  // A4 — Group by slot when "Créneau seul" is off
  const groups = useMemo(() => {
    if (onlySlot) {
      return [{ key: selectedTime ?? 'all', label: null as string | null, items: filtered }];
    }
    const byTime = new Map<string, Reservation[]>();
    const unassigned: Reservation[] = [];
    for (const r of filtered) {
      if (!r.tableId && !r.combinedTableId) { unassigned.push(r); continue; }
      const k = r.startTime.substring(0, 5);
      const arr = byTime.get(k) ?? [];
      arr.push(r);
      byTime.set(k, arr);
    }
    const sortedKeys = [...byTime.keys()].sort();
    const result: { key: string; label: string | null; items: Reservation[] }[] = [];
    if (unassigned.length > 0) {
      result.push({ key: '__unassigned__', label: 'À placer', items: unassigned });
    }
    for (const k of sortedKeys) {
      result.push({ key: k, label: k, items: byTime.get(k)! });
    }
    return result;
  }, [onlySlot, selectedTime, filtered]);

  const tableNameFor = (r: Reservation): string | null => {
    if (r.tableId) return tables.find(t => t.id === r.tableId)?.name ?? null;
    if (r.combinedTableId) return combinedTables.find(c => c.id === r.combinedTableId)?.name ?? null;
    return null;
  };

  const toggleSlot = (key: string) => {
    setCollapsedSlots(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const renderCard = (r: Reservation) => {
    const tName = tableNameFor(r);
    const unassigned = !tName;
    const isHl = highlightedReservationId === r.id;
    const isBeingDragged = dragState.isDragging && dragState.draggedReservation?.id === r.id;
    const draggable = !!onTableChange && r.status !== 'annulé' && !r.isLocked;
    const late = isToday && isLate(r, today, nowMin);
    return (
      <V2ReservationCard
        key={r.id}
        reservation={r}
        draggable={draggable}
        onDragStart={() => startDrag(r)}
        onDragEnd={() => endDrag()}
        className={cn(
          "p-2 rounded-md border bg-background text-xs transition-all",
          draggable && "cursor-grab active:cursor-grabbing",
          r.isLocked && !draggable && onTableChange && "cursor-not-allowed",
          isHl && "ring-2 ring-primary border-primary/60 shadow-sm",
          isBeingDragged && "opacity-50 scale-95",
          r.status === 'annulé' && "opacity-60",
          r.isLocked && "border-foreground/40 bg-muted/30",
          late && "border-orange-500/60 bg-orange-50/60 dark:bg-orange-950/30 animate-pulse",
        )}
        onMouseEnter={() => onHoverReservation(r.id)}
        onMouseLeave={() => onHoverReservation(null)}
        onClick={() => onSelectReservation(r)}
        onDoubleClick={() => onEditReservation?.(r)}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-1.5 min-w-0">
            {draggable && <GripVertical className="w-3 h-3 text-muted-foreground/50 shrink-0" />}
            <Badge
              variant="outline"
              className="text-[10px] px-1.5 py-0 h-4 font-mono shrink-0 gap-1"
              style={{ borderColor: getSlotColor(r.startTime.substring(0, 5), { saturation: 60, lightness: 55, alpha: 0.6 }) }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: getSlotColor(r.startTime.substring(0, 5)) }}
              />
              {r.startTime.substring(0, 5)}
            </Badge>
            <span className="font-medium truncate">
              {r.customerFirstName} {r.customerLastName}
            </span>
            {r.source === 'walk_in' && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 shrink-0">Walk-in</Badge>
            )}
          </div>
          {late && (
            <Badge className="bg-orange-500 hover:bg-orange-500 text-white h-4 text-[10px] px-1.5 gap-1 shrink-0">
              <ClockIcon className="w-2.5 h-2.5" />
              Retard
            </Badge>
          )}
          {r.isLocked && (
            <Badge variant="outline" className="h-4 text-[10px] px-1 gap-0.5 shrink-0 border-foreground/30">
              <Lock className="w-2.5 h-2.5" />
            </Badge>
          )}
          {r.status === 'arrivé' && (
            <Badge className="bg-green-500/20 text-green-700 border-green-500/30 h-4 text-[10px] px-1.5">Arrivé</Badge>
          )}
          {r.status === 'annulé' && (
            <Badge variant="destructive" className="h-4 text-[10px] px-1.5">Annulé</Badge>
          )}
        </div>

        <div className="flex items-center gap-2 text-muted-foreground flex-wrap">
          {unassigned ? (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-orange-500/10 border-orange-500/40 text-orange-700 dark:text-orange-300 gap-1">
              <AlertCircle className="w-2.5 h-2.5" />
              À placer
            </Badge>
          ) : (
            <span className="flex items-center gap-1">
              <UtensilsCrossed className="w-3 h-3" />
              {tName}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            {r.numberOfPeople}
          </span>
          {(r.numberOfChildren ?? 0) > 0 && (
            <span className="flex items-center gap-0.5" title={`${r.numberOfChildren} enfant(s)`}>
              <Baby className="w-3 h-3" />+{r.numberOfChildren}
            </span>
          )}
          {r.hasStroller && <StrollerIcon size={12} className="text-muted-foreground" />}
          {r.internalNotes && <StickyNote className="w-3 h-3 text-violet-500" />}
        </div>

        {r.notes && (
          <div className="mt-1 flex items-start gap-1 p-1 rounded bg-orange-500/10 text-orange-700 dark:text-orange-400">
            <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
            <span className="line-clamp-1">{r.notes}</span>
          </div>
        )}

        {onStatusChange && r.status !== 'annulé' && (
          <div className="flex items-center gap-1 pt-1.5 border-t mt-1.5">
            <Button
              variant={r.status === 'arrivé' ? 'default' : 'outline'}
              size="sm"
              className={cn(
                "flex-1 h-6 text-[10px] px-1",
                r.status === 'arrivé' && "bg-green-600 hover:bg-green-700"
              )}
              onClick={(e) => {
                e.stopPropagation();
                if (r.status !== 'arrivé') onStatusChange(r, 'arrivé');
              }}
            >
              <Check className="w-3 h-3 mr-0.5" /> Arrivé
            </Button>
            <Button
              variant={r.status === 'planifié' ? 'default' : 'outline'}
              size="sm"
              className={cn(
                "flex-1 h-6 text-[10px] px-1",
                r.status === 'planifié' && "bg-blue-600 hover:bg-blue-700"
              )}
              onClick={(e) => {
                e.stopPropagation();
                if (r.status !== 'planifié') onStatusChange(r, 'planifié');
              }}
            >
              <CalendarClock className="w-3 h-3 mr-0.5" /> Planifié
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-6 text-[10px] px-1"
              onClick={(e) => {
                e.stopPropagation();
                onStatusChange(r, 'annulé');
              }}
            >
              <X className="w-3 h-3 mr-0.5" /> Annuler
            </Button>
            {onLockChange && (
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-6 w-7 text-[10px] px-0 shrink-0",
                  r.isLocked && "bg-foreground/10 border-foreground/40"
                )}
                title={r.isLocked ? 'Déverrouiller' : 'Verrouiller cette assignation'}
                onClick={(e) => {
                  e.stopPropagation();
                  onLockChange(r.id, !r.isLocked);
                }}
              >
                {r.isLocked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
              </Button>
            )}
          </div>
        )}
      </V2ReservationCard>
    );
  };

  const showGroupHeaders = !onlySlot && groups.length > 1;

  return (
    <div className="flex flex-col h-full bg-card border-r">
      {/* Header */}
      <div className="shrink-0 px-3 py-2 border-b space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <UtensilsCrossed className="w-4 h-4" />
            Réservations
          </h3>
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {counters.covers} couv.
          </span>
        </div>
        {/* A3 — counters */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="outline" className="h-5 px-1.5 text-[10px] gap-1 bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-300">
            <Check className="w-2.5 h-2.5" />
            {counters.arrived} arrivés
          </Badge>
          <Badge variant="outline" className="h-5 px-1.5 text-[10px] gap-1 bg-blue-500/10 border-blue-500/40 text-blue-700 dark:text-blue-300">
            <CalendarClock className="w-2.5 h-2.5" />
            {counters.upcoming} à venir
          </Badge>
          {isToday && (
            <Badge
              variant="outline"
              className={cn(
                "h-5 px-1.5 text-[10px] gap-1 border-orange-500/40 text-orange-700 dark:text-orange-300",
                counters.late > 0 ? "bg-orange-500/20 animate-pulse" : "bg-orange-500/5 opacity-60"
              )}
            >
              <ClockIcon className="w-2.5 h-2.5" />
              {counters.late} retard
            </Badge>
          )}
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            <Switch
              id="v2rp-onlyslot"
              checked={onlySlot}
              onCheckedChange={setOnlySlot}
              className="scale-75"
            />
            <Label htmlFor="v2rp-onlyslot" className="text-[11px] cursor-pointer">
              Créneau seul
            </Label>
          </div>
          <div className="flex items-center gap-1.5">
            <Switch
              id="v2rp-hidecan"
              checked={hideCancelled}
              onCheckedChange={setHideCancelled}
              className="scale-75"
            />
            <Label htmlFor="v2rp-hidecan" className="text-[11px] cursor-pointer">
              Cacher annulées
            </Label>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            <UtensilsCrossed className="w-7 h-7 mx-auto mb-2 opacity-40" />
            Aucune réservation
          </div>
        ) : (
          <div className="p-1.5 space-y-2">
            {groups.map(g => {
              const collapsed = collapsedSlots.has(g.key);
              const groupCovers = g.items.reduce(
                (s, r) => s + (r.status !== 'annulé' ? r.numberOfPeople : 0),
                0
              );
              const groupLate = isToday
                ? g.items.filter(r => isLate(r, today, nowMin)).length
                : 0;
              return (
                <div key={g.key} className="space-y-1">
                  {showGroupHeaders && g.label && (
                    <button
                      type="button"
                      onClick={() => toggleSlot(g.key)}
                      className={cn(
                        "w-full flex items-center justify-between gap-2 px-2 py-1 rounded-md text-[11px] font-medium",
                        "bg-muted/60 hover:bg-muted transition-colors sticky top-0 z-10",
                        g.key === '__unassigned__' && "bg-orange-500/10 hover:bg-orange-500/20 text-orange-700 dark:text-orange-300",
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        {collapsed
                          ? <ChevronRight className="w-3 h-3" />
                          : <ChevronDown className="w-3 h-3" />}
                        {g.key !== '__unassigned__' && (
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ background: getSlotColor(g.key) }}
                          />
                        )}
                        <span className="font-mono">{g.label}</span>
                        <span className="text-muted-foreground font-normal">
                          · {g.items.length} rés · {groupCovers}c
                        </span>
                      </span>
                      {groupLate > 0 && (
                        <span className="flex items-center gap-0.5 text-orange-600 dark:text-orange-400">
                          <ClockIcon className="w-2.5 h-2.5" />
                          {groupLate}
                        </span>
                      )}
                    </button>
                  )}
                  {!collapsed && (
                    <div className="space-y-1.5">
                      {g.items.map(renderCard)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};
